// API client of the admin console. Every request goes to one TARGET instance:
// this one (the session's own backend and token) or another registered
// instance a super admin connected to with an account there. The browser talks
// to that instance directly — its data and credentials never pass this one.

import { API_BASE_URL, getToken, getUser } from '../auth';

const REMOTE_SESSIONS_KEY = 'admin_remote_sessions';

export const ownTarget = () => ({
  key: '', label: 'This instance', base: API_BASE_URL, token: getToken(), me: getUser() || {}, remote: false,
});

export class ApiError extends Error {
  constructor(message, status, fields) {
    super(message);
    this.status = status;
    this.fields = fields || [];
  }
}

export function createClient(target) {
  const request = async (method, path, body) => {
    const headers = { ...(target.token ? { Authorization: `Bearer ${target.token}` } : {}) };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let res;
    try {
      res = await fetch(`${target.base}${path}`, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
    } catch (e) {
      throw new ApiError(`${target.remote ? target.label : 'Server'} not reachable`, 0);
    }
    let data = null;
    try { data = await res.json(); } catch (e) { /* empty body */ }
    if (!res.ok) {
      if (res.status === 401 && target.remote) forgetRemoteSession(target.key);
      throw new ApiError(data?.error || `Request failed (${res.status})`, res.status, data?.fields);
    }
    return data;
  };
  return {
    target,
    get: (path) => request('GET', path),
    post: (path, body = {}) => request('POST', path, body),
    put: (path, body = {}) => request('PUT', path, body),
    del: (path) => request('DELETE', path),
  };
}

// ── Sessions on other instances (kept for this browser tab only) ──
const readSessions = () => {
  try { return JSON.parse(sessionStorage.getItem(REMOTE_SESSIONS_KEY) || '{}'); } catch (e) { return {}; }
};
const writeSessions = (s) => {
  try { sessionStorage.setItem(REMOTE_SESSIONS_KEY, JSON.stringify(s)); } catch (e) { /* storage blocked */ }
};

export const remoteSession = (key) => readSessions()[key] || null;

export const forgetRemoteSession = (key) => {
  const s = readSessions();
  delete s[key];
  writeSessions(s);
};

/** Log in on another instance with an account there (admin or super admin). */
export async function connectRemote(instance, username, password) {
  let res;
  try {
    res = await fetch(`${instance.url}/api/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password, via: 'console' }),
    });
  } catch (e) {
    throw new ApiError(`${instance.label} (${instance.url}) is not reachable from this browser`, 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.error || 'Login failed', res.status);
  if (data.login_at) throw new ApiError(`This account is served by ${data.instance} there — use an account of ${instance.label}`, 400);
  if (!data.user?.is_admin) throw new ApiError(`${username} is not an administrator on ${instance.label}`, 403);
  const s = readSessions();
  s[instance.key] = { token: data.token, me: data.user };
  writeSessions(s);
  return s[instance.key];
}

export const remoteTarget = (instance, session) => ({
  key: instance.key, label: instance.label, base: instance.url, token: session.token, me: session.me, remote: true,
  environment: instance.environment,
});
